import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  Injector,
  Type,
  ViewContainerRef
} from '@angular/core';
import { DialogComponent } from '@bca-core/modules/dialog/dialog/dialog.component';
import { IDialogConfig } from '@bca-core/modules/dialog/models/dialog-config';
import { DIALOG_PROPERTIES } from '@bca-core/modules/dialog/models/dialog-props';
import { DialogRef } from '@bca-core/modules/dialog/models/dialog-ref';
import { DialogId } from '@bca-shared/models/_types';
import { Subject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

export class DialogContainer<T, P> {
  private _afterClosed = new Subject<void>();
  private _beforeClosed = new Subject<T>();
  private _backdropClicked = new Subject<void>();
  private _escapeKeyPressed = new Subject<void>();

  private _host: ComponentRef<DialogComponent>;
  private _container: ComponentRef<T>;

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    private _document: HTMLDocument
  ) {}

  create(
    componentContainer: Type<T>,
    identifier: DialogId,
    config: IDialogConfig,
    props?: any
  ): DialogRef<T> {
    this._host = this._createHost(config, this._viewContainerRef);
    this._container = this._createContainer(componentContainer, props, this._viewContainerRef);

    this._attach(config);

    this._host.instance
      .closeClicked()
      .pipe(takeWhile(() => this._host != null))
      .subscribe(() => {
        this._beforeClosed.next(this._container.instance);
        this._beforeClosed.complete();
        this._destroy();
      });

    this._host.instance
      .backdropClicked()
      .pipe(takeWhile(() => this._host != null))
      .subscribe(() => {
        this._backdropClicked.next();
        if (config.backdropShouldClose) {
          this._host.instance.close.next();
          this._backdropClicked.complete();
        }
      });

    this._host.instance
      .escapeKeyPressed()
      .pipe(takeWhile(() => this._host != null))
      .subscribe(() => {
        this._escapeKeyPressed.next();
        if (config.escapeKeyShouldClose) {
          this._host.instance.close.next();
          this._escapeKeyPressed.complete();
        }
      });

    return new DialogRef<T>(
      this._container.instance,
      identifier,
      this._afterClosed,
      this._beforeClosed,
      this._backdropClicked,
      this._escapeKeyPressed,
      () => this._host.instance.close.next()
    );
  }

  private _createContainer<T>(componentContainer: Type<T>, props: any, viewContainerRef: ViewContainerRef): ComponentRef<T> {
    const injector = Injector.create({
      providers: [
        {
          provide: DIALOG_PROPERTIES,
          useValue: props
        }
      ],
      parent: this._injector
    });
    
    const componentRef = viewContainerRef.createComponent(componentContainer, { injector });
    
    return componentRef;
  }

  private _createHost({
    disableClose,
    disableAnimation,
    disableBackdrop
  }: IDialogConfig, viewContainerRef: ViewContainerRef): ComponentRef<DialogComponent> {
    const dialogHost = viewContainerRef.createComponent(DialogComponent);
    dialogHost.instance.disableClose = disableClose;
    dialogHost.instance.disableAnimation = disableAnimation;
    dialogHost.instance.disableBackdrop = disableBackdrop;
  
    return dialogHost;
  }
  

  private _attach({ scrollableBackground }: IDialogConfig): void { 
    const dialogHostElement = [
      ...(this._host.hostView as EmbeddedViewRef<DialogComponent>).rootNodes
    ].shift() as HTMLElement;
    
    const componentRefElement = [
      ...(this._container.hostView as EmbeddedViewRef<T>).rootNodes
    ].shift() as HTMLElement;
  
    dialogHostElement.querySelector('.dialog-container').appendChild(componentRefElement);
  
    if (!componentRefElement.classList.contains('dialog-body')) {
      componentRefElement.classList.add('dialog-body');
    }
  
    this._document.body.appendChild(dialogHostElement);
  
    if (!scrollableBackground && !this._document.body.classList.contains('dialog-open')) {
      this._document.body.classList.add('dialog-open');
    }
  }

  private _destroy(): void {
    this._applicationRef.detachView(this._container.hostView);
    this._container.destroy();
    this._applicationRef.detachView(this._host.hostView);
    this._host.destroy();
    this._container = null;
    this._host = null;
    this._afterClosed.next();
    this._afterClosed.complete();
    if (this._document.body.classList.contains('dialog-open')) {
      this._document.body.classList.remove('dialog-open');
    }
  }
}
